import React, { Fragment, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { Breadcrumbs, H5 } from "../../AbstractElements";
import { cancelReservation, getReservation, resendReservationConfirmation } from "../../api/reservation.api";
import { titleCase } from "../../utils/case";
import { generateAccessPermit, sendAccessPermit } from "../../api/accessPermit.api";
import { hasRole } from "../../utils/auth";

const ReservationView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canManageReservations = hasRole("superadmin", "finance");
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAppliedPromotions = (item) =>
    Array.isArray(item?.discountApplied) ? item.discountApplied : [];

  const getPromoCodes = (item) => {
    const codes = getAppliedPromotions(item)
      .map((promotion) => promotion?.promoCode)
      .filter(Boolean);
    return [...new Set(codes)].join(", ") || "-";
  };

  const getDiscountAmount = (item) =>
    getAppliedPromotions(item).reduce(
      (total, promotion) => total + (Number(promotion?.amount) || 0),
      0
    );


  const fetchReservation = async () => {
    try {
      setLoading(true);
      const res = await getReservation(id);
      setReservation(res);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservation();
  }, [id]);

  if (loading) {
    return (
      <Container fluid>
        Loading...
      </Container>
    );
  }
  
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleDownloadPermit = async () => {
    try {
      const { filePath } = await generateAccessPermit(reservation.id);
      window.open(`${process.env.REACT_APP_FILES_URL}/${filePath}`, "_blank");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSendPermit = async () => {
    if (!window.confirm(t("Send access permit to customer?"))) return;

    try {
      await sendAccessPermit(reservation.id);
      toast.success(t("Access permit sent successfully."));
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("reservations"))}
        title={titleCase(t("reservation details"))}
        mainTitle={
          <div className="d-flex align-items-center gap-2">
            <span>{reservation?.bookingRef}</span>
            <Badge
              className="m-l-5"
              color={reservation?.status === "confirmed" ? "success" : "dark"}
            >
              {titleCase(t(reservation?.status))}
            </Badge>
          </div>
        }
      />

      <Container fluid>
        <Row className="mb-4">
          <Col className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
            <button
              className="btn btn-sm btn-light"
              onClick={() => scrollToSection("guests")}
            >
              {titleCase(t("guests & payment"))}
            </button>

            <div
              style={{
                width: "1px",
                height: "24px",
                backgroundColor: "#ddd",
                margin: "0 8px",
              }}
            />

            {canManageReservations && (
              <button
                className="btn btn-sm btn-primary"
                onClick={() => navigate(`/reservations/edit/${id}`)}
              >
                {titleCase(t("edit"))}
              </button>
            )}

            {canManageReservations && reservation.status !== "cancelled" && (
              <button
                className="btn btn-sm btn-danger"
                onClick={async () => {
                  const confirmed = window.confirm(
                    t("Are you sure you want to cancel this reservation?")
                  );
                  if (!confirmed) return;
                  try {
                    await cancelReservation(id);
                    toast.success(t("Reservation cancelled successfully."));
                    fetchReservation();
                  } catch (err) {
                    console.log(err);
                    toast.error(err.message);
                  }
                }}
              >
                {titleCase(t("cancel"))}
              </button>
            )}

            <button
              className="btn btn-sm btn-dark"
              onClick={() => navigate(-1)}
            >
              {titleCase(t("back"))}
            </button>
          </Col>
        </Row>
        
        <Row className="mb-3">
          <Col xl="8" id="reservation-information">
            <Card className="h-100">
              <CardHeader>
                <H5 className="mb-0">
                  {titleCase(t("reservation details"))}
                </H5>
              </CardHeader>

              <CardBody>
                <div className="table-responsive mb-4">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>{titleCase(t("item"))}</th>

                        <th
                          className="text-center"
                          width={100}
                        >
                          {titleCase(t("quantity"))}
                        </th>

                        <th>{titleCase(t("promo code"))}</th>

                        {canManageReservations && (
                          <th className="text-end" width={150}>
                            {titleCase(t("discount amount"))}
                          </th>
                        )}

                        {canManageReservations && (
                          <th
                            className="text-end"
                            width={150}
                          >
                            {titleCase(t("amount"))}
                          </th>
                        )}
                      </tr>
                    </thead>

                    <tbody>
                      {reservation?.orderItems
                        ?.filter((x) => x.itemType === "package")
                        .sort((a, b) => a.itemName.localeCompare(b.itemName))
                        .map((item) => (
                          <tr key={item.id}>
                            <td>
                              <strong>{item.itemName}</strong>

                              {item.description && (
                                <div className="text-muted small">
                                  {item.description}
                                </div>
                              )}
                            </td>

                            <td className="text-center">
                              {item.quantity}
                            </td>

                            <td className="text-break">
                              {getPromoCodes(item)}
                            </td>

                            {canManageReservations && (
                              <td className="text-end">
                                RM {getDiscountAmount(item).toFixed(2)}
                              </td>
                            )}

                            {canManageReservations && (
                              <td className="text-end">
                                RM {Number(item.finalCost ?? 0).toFixed(2)}
                              </td>
                            )}
                          </tr>
                        ))}

                      {reservation?.orderItems
                        ?.filter((x) => x.itemType === "lot")
                        .sort((a, b) => a.itemName.localeCompare(b.itemName))
                        .map((item) => (
                          <tr key={item.id}>
                            <td>
                              <strong>{item.itemName}</strong>

                              {item.description && (
                                <div className="text-muted small">
                                  {item.description}
                                </div>
                              )}
                            </td>

                            <td className="text-center">
                              {item.quantity}
                            </td>
                            
                            <td className="text-break">
                              {getPromoCodes(item)}
                            </td>

                            {canManageReservations && (
                              <td className="text-end">
                                RM {getDiscountAmount(item).toFixed(2)}
                              </td>
                            )}

                            {canManageReservations && (
                              <td className="text-end">
                                RM {Number(item.finalCost ?? 0).toFixed(2)}
                              </td>
                            )}
                          </tr>
                        ))}

                      {reservation?.orderItems
                        ?.filter((x) => x.itemType === "add_on")
                        .sort((a, b) => a.itemName.localeCompare(b.itemName))
                        .map((item) => (
                          <tr key={item.id}>
                            <td>
                              <strong>{item.itemName}</strong>

                              {item.description && (
                                <div className="text-muted small">
                                  {item.description}
                                </div>
                              )}
                            </td>

                            <td className="text-center">
                              {item.quantity}
                            </td>

                            <td className="text-break">
                              {getPromoCodes(item)}
                            </td>

                            {canManageReservations && (
                              <td className="text-end">
                                RM {getDiscountAmount(item).toFixed(2)}
                              </td>
                            )}

                            {canManageReservations && (
                              <td className="text-end">
                                RM {Number(item.finalCost ?? 0).toFixed(2)}
                              </td>
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <Row>
                  <Col md="6">
                    <div className="p-3 border rounded mb-3">
                      <Row className="align-items-center">
                        <Col md="3">
                          <p className="text-muted mb-0">
                            {titleCase(t("date"))}
                          </p>
                        </Col>
                        <Col md="9">
                          <h6 className="mb-0">{reservation?.startDate}-{reservation?.endDate}</h6>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="p-3 border rounded mb-3">
                      <Row className="align-items-center">
                        <Col md="4">
                          <p className="text-muted mb-0">
                            {titleCase(t("location"))}
                          </p>
                        </Col>
                        <Col md="8">
                          <h6 className="mb-0">
                            {reservation?.orderItems.find((i) => {
                              return i.itemType == "lot"
                            })?.lot.location}
                          </h6>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                  <Col xl="12">
                    <div className="p-3 border rounded mb-3">
                      <Row className="align-items-center">
                        <Col md="12" className="mb-2">
                          <p className="text-muted mb-0">
                            {titleCase(t("remarks"))}
                          </p>
                        </Col>
                        <Col md="12">
                          <h6 className="mb-0">
                            {reservation?.remark ?? "-"}
                          </h6>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4" id="contact-details">
            <Card className="h-100">
              <CardHeader>
                <H5 className="mb-0">
                  {titleCase(t("Contact Details"))}
                </H5>
              </CardHeader>

              <CardBody>
                <div className="p-3 border rounded mb-3">
                  <Row className="align-items-center">
                    <Col md="4">
                      <p className="text-muted mb-0">
                        {titleCase(t("name"))}
                      </p>
                    </Col>
                    <Col md="8">
                      <h6 className="mb-0">{reservation?.name}</h6>
                    </Col>
                  </Row>
                </div>
                <div className="p-3 border rounded mb-3">
                  <Row className="align-items-center">
                    <Col md="4">
                      <p className="text-muted mb-0">
                        {titleCase(t("phone no"))}
                      </p>
                    </Col>
                    <Col md="8">
                      <h6 className="mb-0">{reservation?.phoneNo}</h6>
                    </Col>
                  </Row>
                </div>
                <div className="p-3 border rounded mb-3">
                  <Row className="align-items-center">
                    <Col md="4">
                      <p className="text-muted mb-0">
                        {titleCase(t("email"))}
                      </p>
                    </Col>
                    <Col md="8">
                      <h6 className="mb-0">{reservation?.email}</h6>
                    </Col>
                  </Row>
                </div>

                <Button
                  color="outline-primary"
                  className="w-100 mt-3"
                  onClick={async () => {
                    const confirmed = window.confirm(t("Are you sure you want to resend the reservation confirmation email?"));
                    if (!confirmed) return;
                    try {
                      await resendReservationConfirmation(reservation.id);
                      toast.success(t("Reservation confirmation email sent successfully."));
                    } catch (err) {
                      toast.error(err.message);
                    }
                  }}
                >
                  {titleCase(t("resend confirmation"))}
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col xl="8" id="guests">
            <Card className="h-100">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <H5 className="mb-0">{titleCase(t("guests"))}</H5>

                <div className="d-flex gap-2">
                  {[
                    {
                      color: "primary",
                      icon: "download",
                      label: "download permit",
                      onClick: handleDownloadPermit,
                    },
                    {
                      color: "secondary",
                      icon: "paper-plane",
                      label: "email permit",
                      onClick: handleSendPermit,
                    },
                  ].map(({ color, icon, label, onClick }) => (
                    <Button key={label} color={color} size="sm" onClick={onClick}>
                      <i className={`fa fa-${icon} me-2`} />
                      {titleCase(t(label))}
                    </Button>
                  ))}
                </div>
              </CardHeader>

              <CardBody>
                {reservation?.guests?.length ? (
                  reservation.guests.map((guest, index) => (
                    <div
                      key={guest.id}
                      className="border rounded p-3 mb-3"
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">
                          {titleCase(t("guest"))} #{index + 1}
                        </h6>

                        <Badge color="success">
                          {titleCase(t(guest.status))}
                        </Badge>
                      </div>

                      <Row>
                        {/* Personal Details */}
                        <Col md="6">
                          <h6 className="mb-3 text-primary">
                            {titleCase(t("personal details"))}
                          </h6>

                          <table className="table table-borderless table-sm mb-0">
                            <tbody>
                              <tr>
                                <td width={150}>
                                  <strong>{titleCase(t("name"))}</strong>
                                </td>
                                <td>{guest.name || "-"}</td>
                              </tr>

                              <tr>
                                <td>
                                  <strong>{titleCase(t("nric / passport"))}</strong>
                                </td>
                                <td>{guest.nricPassportNo || "-"}</td>
                              </tr>

                              <tr>
                                <td>
                                  <strong>{titleCase(t("phone no"))}</strong>
                                </td>
                                <td>{guest.phoneNo || "-"}</td>
                              </tr>
                            </tbody>
                          </table>
                        </Col>

                        {/* Vehicle Details */}
                        <Col md="6">
                          <h6 className="mb-3 text-primary">
                            {titleCase(t("vehicle details"))}
                          </h6>

                          <table className="table table-borderless table-sm mb-0">
                            <tbody>
                              <tr>
                                <td width={140}>
                                  <strong>{titleCase(t("plate no"))}</strong>
                                </td>
                                <td>{guest.carPlateNo || "-"}</td>
                              </tr>

                              <tr>
                                <td>
                                  <strong>{titleCase(t("brand"))}</strong>
                                </td>
                                <td>{guest.carBrand || "-"}</td>
                              </tr>

                              <tr>
                                <td>
                                  <strong>{titleCase(t("model"))}</strong>
                                </td>
                                <td>{guest.carModel || "-"}</td>
                              </tr>

                              <tr>
                                <td>
                                  <strong>{titleCase(t("color"))}</strong>
                                </td>
                                <td>{guest.carColor || "-"}</td>
                              </tr>
                            </tbody>
                          </table>
                        </Col>
                      </Row>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted py-5">
                    {titleCase(t("no guests"))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col xl="4" id="transactions">
            <Card className="h-100">
              <CardHeader>
                <H5 className="mb-0">
                  {titleCase(t("payment details"))}
                </H5>
              </CardHeader>

              <CardBody>
                <div className="p-3 border rounded mb-3">
                  <Row className="align-items-center">
                    <Col md="5">
                      <p className="text-muted mb-0">
                        {titleCase(t("payment status"))}
                      </p>
                    </Col>

                    <Col md="7">
                      <Badge
                        color={
                          reservation?.transaction?.status === "success"
                            ? "success"
                            : reservation?.transaction?.status === "pending"
                            ? "warning"
                            : "danger"
                        }
                      >
                        {titleCase(t(reservation?.transaction?.status || "-"))}
                      </Badge>
                    </Col>
                  </Row>
                </div>

                <div className="p-3 border rounded mb-3">
                  <Row className="align-items-center">
                    <Col md="5">
                      <p className="text-muted mb-0">
                        {titleCase(t("amount"))}
                      </p>
                    </Col>

                    <Col md="7">
                      <h6 className="mb-0">
                        RM {Number(reservation?.transaction?.amount ?? 0).toFixed(2)}
                      </h6>
                    </Col>
                  </Row>
                </div>

                <div className="p-3 border rounded mb-3">
                  <Row className="align-items-center">
                    <Col md="5">
                      <p className="text-muted mb-0">
                        {titleCase(t("payment channel"))}
                      </p>
                    </Col>

                    <Col md="7">
                      <h6 className="mb-0">
                        {titleCase(
                          reservation?.transaction?.paymentChannel || "-"
                        )}
                      </h6>
                    </Col>
                  </Row>
                </div>

                <div className="p-3 border rounded mb-3">
                  <Row className="align-items-center">
                    <Col md="5">
                      <p className="text-muted mb-0">
                        {titleCase(t("reference no"))}
                      </p>
                    </Col>

                    <Col md="7">
                      <h6
                        className="mb-0 text-break"
                        style={{ fontSize: "0.9rem" }}
                      >
                        {reservation?.transaction?.refNo || "-"}
                      </h6>
                    </Col>
                  </Row>
                </div>

                <div className="p-3 border rounded">
                  <Row className="align-items-center">
                    <Col md="5">
                      <p className="text-muted mb-0">
                        {titleCase(t("paid at"))}
                      </p>
                    </Col>

                    <Col md="7">
                      <h6 className="mb-0">
                        {reservation?.transaction?.paidAt || "-"}
                      </h6>
                    </Col>
                  </Row>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ReservationView;